import { toast } from 'react-toastify';
import { Button } from '@accure/ui';
import { saveAs } from 'file-saver';
import { Check, ShieldCheck } from 'lucide-react';

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/modules/siteview/components/ui/card';
import { CertificationTile } from '@/modules/trust-center/components/certification-tile';
import { DownloadIso27001DocMutation } from '@/modules/trust-center/mutations/download-iso-27001-doc';
import { DownloadIso27001SoaMutation } from '@/modules/trust-center/mutations/download-iso-27001-soa';

function ExternalLink({
  href,
  children,
  className = 'text-emerald-500 hover:text-emerald-600 hover:underline visited:text-emerald-700 font-medium',
}: {
  href: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <a href={href} target="_blank" rel="noreferrer" className={className}>
      {children}
    </a>
  );
}

type FrameworkSection = {
  title: string;
  items: {
    name: string;
    hasDownload?: boolean;
    externalUrl?: string;
  }[];
};

const securityFramework: FrameworkSection[] = [
  {
    title: 'Policies and Procedures',
    items: [
      { name: 'Information Security Policy', hasDownload: true },
      { name: 'Code of Conduct', hasDownload: true },
      { name: 'Quality Management Policy', hasDownload: true },
    ],
  },
  {
    title: 'Privacy and Data Protection',
    items: [
      { name: 'Cookies', hasDownload: false },
      { name: 'Data Protection Officer', hasDownload: false },
      {
        name: 'Data Processing Addendum',
        hasDownload: true,
      },
      {
        name: 'Privacy Policy',
        hasDownload: false,
        externalUrl: 'https://www.accure.net/privacy',
      },
    ],
  },
  {
    title: 'Infrastructure',
    items: [
      {
        name: 'AWS',
        hasDownload: false,
        externalUrl:
          'https://docs.aws.amazon.com/whitepapers/latest/aws-overview/security-and-compliance.html',
      },
      {
        name: 'Google Cloud Platform',
        hasDownload: false,
        externalUrl: 'https://cloud.google.com/looker/product/security?hl=de',
      },
      { name: 'Business Continuity / Disaster Recovery', hasDownload: true },
      { name: 'Separate Production Environment', hasDownload: false },
      { name: 'Architecture Overview', hasDownload: true },
    ],
  },
  {
    title: 'Network Security',
    items: [
      { name: 'Data Loss Prevention', hasDownload: false },
      { name: 'Firewall', hasDownload: false },
      { name: 'Virtual Private Cloud', hasDownload: false },
      { name: 'Security Event Management', hasDownload: false },
    ],
  },
];

export function TrustCenterPage() {
  const downloadIso27001 = DownloadIso27001DocMutation.useMutation();
  const downloadIso27001Soa = DownloadIso27001SoaMutation.useMutation();

  const handleDownloadISO = async () => {
    const download = downloadIso27001.mutateAsync();
    const { file } = await toast.promise(download, {
      pending: 'Downloading ISO 27001 certificate...',
      success: undefined,
      error: 'Failed to download the ISO 27001 certificate. Please try again.',
    });
    saveAs(file, 'ISO-27001-Certificate.pdf');
  };

  const handleDownloadSOA = async () => {
    const download = downloadIso27001Soa.mutateAsync();
    const { file } = await toast.promise(download, {
      pending: 'Downloading ISO 27001 SOA...',
      success: undefined,
      error: 'Failed to download the ISO 27001 SOA. Please try again.',
    });
    saveAs(file, 'ISO-27001-SOA.pdf');
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
      {/* Main Content */}
      <div className="mb-8 text-center">
        <h2 className="mb-4 text-2xl font-bold sm:text-3xl">Trust Center</h2>
        <p className="mx-auto max-w-3xl text-gray-600">
          The security and privacy of your data are our top priorities. We
          maintain the highest standards of security, compliance, and
          transparency to protect your data and ensure your trust.
        </p>
      </div>

      {/* Security Certifications */}
      <div className="mb-8">
        <h3 className="mb-2 text-lg font-semibold sm:text-xl">Compliance</h3>
        <p className="mb-6 text-gray-600">
          We maintain a management system in accordance to industry-leading
          security certifications and compliance standards.
        </p>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {/* ISO 27001 - With Download */}
          <CertificationTile
            title="ISO 27001"
            description="Information Security Management System"
            status="Certified"
            downloadHandler={handleDownloadISO}
            isDownloading={downloadIso27001.isLoading}
            downloadText="Download Certificate"
          />

          {/* ISO 27001 SoA - With Download */}
          <CertificationTile
            title="ISO 27001 SoA"
            description="Statement of Applicability"
            status="Certified"
            downloadHandler={handleDownloadSOA}
            isDownloading={downloadIso27001Soa.isLoading}
            downloadText="Download Certificate"
          />

          {/* SOC 2 Type II - No Download */}
          <CertificationTile
            title="SOC 2 Type II"
            description="Service Organization Control 2"
            status="Compliant"
          />

          {/* AESCSF - No Download */}
          <CertificationTile
            title="AESCSF"
            description="Australian Energy Sector Cyber Security Framework"
            status="Compliant"
          />
        </div>
      </div>

      {/* Security Framework */}
      <div>
        <h3 className="mb-2 text-lg font-semibold sm:text-xl">
          Compliance Controls
        </h3>
        <p className="mb-6 text-gray-600">
          Our comprehensive security measures in all critical areas of our
          infrastructure and operations include the following, among others.
        </p>
        {/* Security Contact */}
        <div className="grid grid-cols-1 gap-4 sm:gap-6 lg:grid-cols-2">
          {securityFramework.map((section, index) => (
            <Card key={index}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base font-semibold sm:text-lg">
                  <ShieldCheck className="size-5 text-emerald-500" />
                  {section.title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {section.items.map((item, itemIndex) => (
                    <li
                      key={itemIndex}
                      className="flex items-start gap-2 text-sm"
                    >
                      <Check className="mt-0.5 size-4 shrink-0 text-emerald-500" />
                      <span className="break-words">
                        {item.externalUrl ? (
                          <ExternalLink href={item.externalUrl}>
                            {item.name}
                          </ExternalLink>
                        ) : (
                          <span className="mr-2">{item.name}</span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
      <div className="mx-auto mt-8 w-full text-center">
        <Card className="border border-gray-200 bg-gray-50">
          <CardHeader>
            <CardTitle className="flex items-center justify-center gap-2 text-lg font-semibold">
              <ShieldCheck className="size-5 text-emerald-500" />
              Security Contact
            </CardTitle>
            <p className="mt-1 text-sm text-gray-600">
              Have questions about our security practices or need to report a
              security issue?
            </p>
          </CardHeader>
          <CardContent className="mt-auto space-y-4">
            <Button
              size="sm"
              className="mb-2 px-8 text-white"
              onClick={() => {
                window.location.href = 'mailto:dataprotection@accure.net';
              }}
            >
              Contact Security Team
            </Button>
            <p className="mb-2 text-sm text-gray-600">
              We typically respond to security inquiries within 24 hours
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
